import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useLinkedBoardsTaskStats } from '../hooks/use-linked-boards-task-stats';
import { useExtensionsStore } from '@/stores/extensions';
import type { ProjectDetail } from '@hygge/shared';
import { BarChart3, CheckCircle2, ExternalLink, ListTodo, Loader2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';

interface ProgressTabProps {
  project: ProjectDetail;
}

export function ProgressTab({ project }: ProgressTabProps) {
  // "Not enabled" only when the extensions list says so: not while it is still
  // loading, and not to a reader refused it (403) — see the Dashboard card.
  const isTaskManagerEnabled = useExtensionsStore(
    (s) => s.isExtensionEnabled('task-manager') || !s.isLoaded || s.loadFailed,
  );
  // Task stats are a board's, asked for by the board id. This passed the
  // project id, which names no board: the card read zero for everybody, and once
  // board routes checked access it read "not connected" for anyone but a
  // super_admin. A project can have several boards (HW-983); the figures are
  // those of every linked board the reader can open, added up.
  const { boardIds, stats, isLoading, isBoardsError, isStatsError } = useLinkedBoardsTaskStats(
    project.id,
    isTaskManagerEnabled,
  );

  if (!isTaskManagerEnabled) {
    return (
      <div className="rounded-lg border border-dashed border-border p-12 text-center">
        <ListTodo className="h-6 w-6 text-muted-foreground/40 mx-auto mb-2" />
        <h3 className="text-sm font-medium text-foreground mb-1">Task Manager not enabled</h3>
        <p className="text-xs text-muted-foreground max-w-sm mx-auto">
          Enable the Task Manager extension in Settings &rarr; Integrations to see task progress analytics.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-48 rounded-lg" />
      </div>
    );
  }

  // A request that failed says nothing about the boards: "no board you can
  // open" would be a claim it cannot make. Figures already loaded stay on
  // screen when only a background refresh fails, as on the Dashboard card.
  if (isBoardsError || isStatsError) {
    return (
      <div className="rounded-lg border border-dashed border-border p-12 text-center">
        <ListTodo className="h-6 w-6 text-muted-foreground/40 mx-auto mb-2" />
        <h3 className="text-sm font-medium text-foreground mb-1">Task progress could not be loaded</h3>
        <p className="text-xs text-muted-foreground max-w-sm mx-auto">Reload the page to try again.</p>
      </div>
    );
  }

  // No board of this project that the caller can open: say so rather than
  // render an empty tab.
  if (boardIds.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-12 text-center">
        <ListTodo className="h-6 w-6 text-muted-foreground/40 mx-auto mb-2" />
        <h3 className="text-sm font-medium text-foreground mb-1">No board to show</h3>
        <p className="text-xs text-muted-foreground max-w-sm mx-auto">
          The task progress here counts the boards linked to this project that you can open, and there
          are none.
        </p>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Tasks"
          value={stats.total}
          icon={<ListTodo className="h-4 w-4" />}
        />
        <StatCard
          label="In Progress"
          value={stats.byCategory.active}
          icon={<Loader2 className="h-4 w-4" />}
          accent="text-blue-500"
        />
        <StatCard
          label="Completed"
          value={stats.byCategory.done}
          icon={<CheckCircle2 className="h-4 w-4" />}
          accent="text-emerald-500"
        />
        <StatCard
          label="Completion"
          value={`${stats.completionPct}%`}
          icon={<BarChart3 className="h-4 w-4" />}
          accent={stats.completionPct >= 75 ? 'text-emerald-500' : stats.completionPct >= 40 ? 'text-amber-500' : undefined}
        />
      </div>

      {/* Status distribution */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">Status Distribution</CardTitle>
            {/* One board opens from here; several are on the Boards tab. */}
            {boardIds.length === 1 ? (
              <Link
                to="/boards"
                search={{ board: boardIds[0] }}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Open Board
                <ExternalLink className="h-3 w-3" />
              </Link>
            ) : (
              <span className="text-xs text-muted-foreground">Across {boardIds.length} boards</span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {stats.total === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <ListTodo className="h-6 w-6 text-muted-foreground/30 mb-2" />
              <p className="text-xs text-muted-foreground">No tasks yet</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Create tasks from the board to see progress here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Stacked bar */}
              <div className="flex h-3 rounded-full overflow-hidden bg-muted">
                {stats.byStatus
                  .filter((s) => s.count > 0)
                  .map((s) => (
                    <div
                      key={s.id}
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${(s.count / stats.total) * 100}%`,
                        backgroundColor: s.color,
                      }}
                      title={`${s.name}: ${s.count}`}
                    />
                  ))}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {stats.byStatus.map((s) => (
                  <div key={s.id} className="flex items-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="text-xs text-muted-foreground">{s.name}</span>
                    <span className="text-xs font-medium text-foreground tabular-nums">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  accent?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
          <span className="text-muted-foreground/50">{icon}</span>
        </div>
        <p className={`text-2xl font-semibold ${accent ?? 'text-foreground'}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}
